import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaProjectDiagram, FaUsers, FaCog, FaArrowRight, FaCheckCircle } from 'react-icons/fa';
import './EngagementPage.css';

const EngagementPage: React.FC = () => {
    const navigate = useNavigate();
    const [scrollY, setScrollY] = useState(0);

    useEffect(() => {
        const handleScroll = () => setScrollY(window.scrollY);
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const models = [
        {
            icon: <FaProjectDiagram />,
            badge: 'Fixed Scope',
            title: 'Project-Based Delivery',
            desc: 'Complete end-to-end delivery with a defined scope, timeline, and set of deliverables. Perfect for one-time implementations with predictable budgets and clearly understood requirements.',
            benefits: ['Fixed scope & deliverables', 'Predictable timeline & cost', 'Dedicated project team', 'Full IP ownership on completion', 'Post-delivery knowledge transfer'],
            ideal: 'New implementations, website builds, system migrations, one-time rollouts',
            img: '/images/api_integration.png'
        },
        {
            icon: <FaUsers />,
            badge: 'Flexible Team',
            title: 'Dedicated Resources',
            desc: 'Extend your in-house team with skilled CoreNuVate professionals who integrate seamlessly with your existing culture, tools, and workflows — scaling up or down as your needs evolve.',
            benefits: ['Flexible scaling model', 'Direct control over the team', 'Cost-effective vs. hiring', 'Swift onboarding process', 'Long-term collaborative growth'],
            ideal: 'Ongoing development, skill gap filling, capacity expansion, long-term roadmaps',
            img: '/images/engagement_team_scaling.png'
        },
        {
            icon: <FaCog />,
            badge: 'Full Management',
            title: 'Managed Services',
            desc: 'We take full ownership of your technology operations — from proactive monitoring to maintenance, updates, and continuous optimisation — so you can focus on your core business.',
            benefits: ['24/7 system monitoring', 'SLA-backed response times', 'Proactive maintenance', 'Regular performance optimisation', 'Dedicated account manager'],
            ideal: 'Ongoing system support, continuous improvements, operational management',
            img: '/images/engagement_managed_dashboard.png'
        },
    ];

    const process = [
        { num: '01', title: 'Initial Consultation', desc: 'We discuss your goals, timelines, team structure, and the challenges you\'re trying to solve.' },
        { num: '02', title: 'Model Selection', desc: 'Together we identify the engagement approach that best fits your budget, scale, and objectives.' },
        { num: '03', title: 'Kickoff & Onboarding', desc: 'We set up communication channels, tools, and a clear roadmap to get started within days.' },
        { num: '04', title: 'Execute & Optimise', desc: 'Agile sprints, regular check-ins, and transparent reporting throughout the entire engagement.' },
    ];

    return (
        <main className="engagement-page">
            {/* Banner */}
            <section className="page-banner parallax-wrap">
                <img
                    src="/images/engagement_hero_bg.png"
                    alt="Engagement Background"
                    className="parallax-bg"
                    style={{ transform: `translateY(${scrollY * 0.2}px)` }}
                />
                <div className="parallax-overlay" />
                <div className="container">
                    <span className="page-banner-tag">How We Work Together</span>
                    <h1 className="page-banner-title fade-in-up">
                        Flexible Engagement<br /><span className="gradient-text">Models for Any Need</span>
                    </h1>
                    <p className="page-banner-subtitle fade-in-up delay-1">
                        We adapt to your business — whether you need a dedicated project team, staff augmentation,
                        or fully managed IT services.
                    </p>
                </div>
            </section>

            {/* Models */}
            <section className="section">
                <div className="container">
                    <div className="models-grid">
                        {models.map((m, i) => (
                            <div key={i} className="glass-card model-card">
                                <div className="model-img-wrap hover-zoom">
                                    <img src={m.img} alt={m.title} className="model-img" />
                                    <div className="model-img-overlay" />
                                </div>
                                <div className="model-content">
                                    <div className="model-header">
                                        <div className="icon-box">{m.icon}</div>
                                        <span className="model-badge">{m.badge}</span>
                                    </div>
                                    <h3>{m.title}</h3>
                                    <p className="model-desc">{m.desc}</p>
                                    <div className="model-benefits">
                                        <h4>Key Benefits</h4>
                                        <ul>
                                            {m.benefits.map(b => (
                                                <li key={b}><FaCheckCircle className="benefit-check" />{b}</li>
                                            ))}
                                        </ul>
                                    </div>
                                    <div className="model-ideal">
                                        <strong>Ideal for:</strong> {m.ideal}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Process */}
            <section className="section engagement-process">
                <div className="container">
                    <div className="text-center mb-64">
                        <span className="section-label">Getting Started</span>
                        <h2 className="section-title">Our Onboarding <span>Process</span></h2>
                    </div>
                    <div className="onboarding-steps">
                        {process.map((p, i) => (
                            <div key={i} className="glass-card onboarding-card">
                                <div className="step-number">{p.num}</div>
                                <h4>{p.title}</h4>
                                <p>{p.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="section">
                <div className="container">
                    <div className="cta-banner">
                        <h2>Not Sure Which Model <span className="gradient-text">Fits Best?</span></h2>
                        <p>Let's schedule a free 30-minute consultation. We'll help you identify the right engagement approach for your specific goals and budget.</p>
                        <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
                            <button className="btn-primary" onClick={() => navigate('/contact')}>
                                <FaArrowRight /> Schedule a Consultation
                            </button>
                            <button className="btn-secondary" onClick={() => navigate('/services')}>
                                View Our Services
                            </button>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default EngagementPage;
